import { db } from "../config";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import type { ContactRecord, ContactsDoc } from "./contact.types";

const sanitizeContacts = (contacts: ContactRecord[]): ContactRecord[] => {
  return contacts
    .map((c) => ({
      name: String(c?.name || "").trim(),
      value: String(c?.value || "").trim(),
    }))
    .filter((c) => c.name.length > 0 && c.value.length > 0);
};

const sortForDisplay = (contacts: ContactRecord[]): ContactRecord[] => {
  const order = ["WhatsApp", "Telegram", "Instagram"];
  const rank = (name: string) => {
    const idx = order.indexOf(name);
    return idx === -1 ? 999 : idx;
  };

  return [...contacts].sort((a, b) => {
    const ra = rank(a.name);
    const rb = rank(b.name);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
};

export class ContactsService {
  private collectionRef = collection(db, "users_contacts");

  async getContacts(uid: string): Promise<ContactRecord[]> {
    const snap = await getDoc(doc(this.collectionRef, uid));
    if (!snap.exists()) return [];

    const data = snap.data() as ContactsDoc;
    const raw = Array.isArray(data.contacts) ? data.contacts : [];

    return sortForDisplay(sanitizeContacts(raw));
  }

  async saveContacts(uid: string, contacts: ContactRecord[]): Promise<void> {
    const sanitized = sanitizeContacts(contacts);

    await setDoc(
      doc(this.collectionRef, uid),
      {
        uid,
        contacts: sanitized,
        updatedAt: serverTimestamp(),
      } satisfies ContactsDoc,
      { merge: true },
    );
  }
}

export const contactsService = new ContactsService();
